/**
 * A minimal Office Open XML workbook (.xlsx) with one sheet, written without a library: a stored
 * (uncompressed) zip of the XML parts. Numbers and booleans are typed cells, ISO dates and
 * date-times become Excel dates, text is stored inline. The header row is bold and frozen.
 */
import type { CsvValue } from './csv';
import { plainCell } from './csv';
import type { ExportGroup } from './tableExport';

export interface SheetTable {
  /** Sheet name (31 characters at most; the rest is cut). */
  name: string;
  headers: readonly string[];
  rows: readonly (readonly CsvValue[])[];
  /** Contiguous groups of rows; each gets a bold heading row above it (then no auto filter). */
  groups?: readonly ExportGroup[];
}

/* ---------- Zip (store only) ---------- */

const CRC_TABLE = new Uint32Array(256).map((_, n) => {
  let c = n;
  for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
  return c >>> 0;
});

export function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (const byte of bytes) crc = (CRC_TABLE[(crc ^ byte) & 0xff] ?? 0) ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}

const encoder = new TextEncoder();

/** The zip's DOS time and date fields for a moment. */
function dosDateTime(date: Date): { time: number; date: number } {
  return {
    time: (date.getHours() << 11) | (date.getMinutes() << 5) | (date.getSeconds() >> 1),
    date:
      ((Math.max(1980, date.getFullYear()) - 1980) << 9) |
      ((date.getMonth() + 1) << 5) |
      date.getDate(),
  };
}

/** Entries stored as given (no compression), with UTF-8 names. */
export function zipStore(
  entries: readonly { name: string; data: Uint8Array }[],
  date = new Date(),
): Uint8Array {
  const { time, date: day } = dosDateTime(date);
  const locals: Uint8Array[] = [];
  const centrals: Uint8Array[] = [];
  let offset = 0;
  for (const entry of entries) {
    const name = encoder.encode(entry.name);
    const crc = crc32(entry.data);
    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, 0x04034b50, true);
    local.setUint16(4, 20, true); // version needed
    local.setUint16(6, 0x0800, true); // UTF-8 names
    local.setUint16(8, 0, true); // stored
    local.setUint16(10, time, true);
    local.setUint16(12, day, true);
    local.setUint32(14, crc, true);
    local.setUint32(18, entry.data.length, true);
    local.setUint32(22, entry.data.length, true);
    local.setUint16(26, name.length, true);
    local.setUint16(28, 0, true);
    const central = new DataView(new ArrayBuffer(46));
    central.setUint32(0, 0x02014b50, true);
    central.setUint16(4, 20, true); // made by
    central.setUint16(6, 20, true); // version needed
    central.setUint16(8, 0x0800, true);
    central.setUint16(10, 0, true);
    central.setUint16(12, time, true);
    central.setUint16(14, day, true);
    central.setUint32(16, crc, true);
    central.setUint32(20, entry.data.length, true);
    central.setUint32(24, entry.data.length, true);
    central.setUint16(28, name.length, true);
    central.setUint16(30, 0, true); // extra
    central.setUint16(32, 0, true); // comment
    central.setUint16(34, 0, true); // disk
    central.setUint16(36, 0, true); // internal attributes
    central.setUint32(38, 0, true); // external attributes
    central.setUint32(42, offset, true);
    locals.push(new Uint8Array(local.buffer), name, entry.data);
    centrals.push(new Uint8Array(central.buffer), name);
    offset += 30 + name.length + entry.data.length;
  }
  const centralSize = centrals.reduce((sum, part) => sum + part.length, 0);
  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, 0x06054b50, true);
  end.setUint16(4, 0, true);
  end.setUint16(6, 0, true);
  end.setUint16(8, entries.length, true);
  end.setUint16(10, entries.length, true);
  end.setUint32(12, centralSize, true);
  end.setUint32(16, offset, true);
  end.setUint16(20, 0, true);
  const parts = [...locals, ...centrals, new Uint8Array(end.buffer)];
  const out = new Uint8Array(parts.reduce((sum, part) => sum + part.length, 0));
  let at = 0;
  for (const part of parts) {
    out.set(part, at);
    at += part.length;
  }
  return out;
}

/* ---------- Sheet XML ---------- */

// eslint-disable-next-line no-control-regex -- XML 1.0 forbids exactly these characters
const INVALID_XML = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\ufffe\uffff]/g;

const escapeXml = (text: string) =>
  text
    .replace(INVALID_XML, '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');

/** "A", "Z", "AA" for a 0-based column index. */
export function columnLetter(index: number): string {
  let n = index + 1;
  let text = '';
  while (n > 0) {
    const rest = (n - 1) % 26;
    text = String.fromCharCode(65 + rest) + text;
    n = Math.floor((n - 1) / 26);
  }
  return text;
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const DATE_TIME = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/;
/** Excel's day 0 (the 1900 leap-year bug makes this the epoch for dates from March 1900 on). */
const EPOCH = Date.UTC(1899, 11, 30);

/** Excel serial number of an ISO date ("2026-01-15") or date-time; null for other text. */
export function excelSerial(text: string): number | null {
  const match = DATE_TIME.exec(text);
  const day = match ? match[1] : DATE.test(text) ? text : null;
  if (!day) return null;
  const [y, m, d] = day.split('-').map(Number);
  if (y === undefined || m === undefined || d === undefined) return null;
  const days = (Date.UTC(y, m - 1, d) - EPOCH) / 86_400_000;
  if (!Number.isFinite(days) || days < 1) return null;
  if (!match) return days;
  const seconds = Number(match[2]) * 3600 + Number(match[3]) * 60 + Number(match[4] ?? 0);
  return days + seconds / 86_400;
}

/** Style indexes of styles.xml below. */
const STYLE = { normal: 0, bold: 1, date: 2, dateTime: 3 } as const;

function cell(ref: string, value: CsvValue, style: number = STYLE.normal): string {
  const s = style === STYLE.normal ? '' : ` s="${style}"`;
  if (value === null || value === undefined) return '';
  if (typeof value === 'number')
    return Number.isFinite(value) ? `<c r="${ref}"${s}><v>${value}</v></c>` : '';
  if (typeof value === 'boolean') return `<c r="${ref}" t="b"${s}><v>${value ? 1 : 0}</v></c>`;
  const text = plainCell(value);
  if (text === '') return '';
  const serial = excelSerial(text);
  if (serial !== null) {
    const kind = DATE.test(text) ? STYLE.date : STYLE.dateTime;
    return `<c r="${ref}" s="${style === STYLE.bold ? STYLE.bold : kind}"><v>${serial}</v></c>`;
  }
  return `<c r="${ref}" t="inlineStr"${s}><is><t xml:space="preserve">${escapeXml(text)}</t></is></c>`;
}

const textLength = (value: CsvValue) =>
  value === null || value === undefined ? 0 : String(value).length;

function sheetXml(table: SheetTable): string {
  const { headers, rows, groups } = table;
  const lines: string[] = [];
  let r = 1;
  lines.push(
    `<row r="${r}">${headers.map((h, i) => cell(`${columnLetter(i)}${r}`, h, STYLE.bold)).join('')}</row>`,
  );
  const dataRow = (row: readonly CsvValue[]) => {
    r += 1;
    lines.push(
      `<row r="${r}">${row.map((v, i) => cell(`${columnLetter(i)}${r}`, v)).join('')}</row>`,
    );
  };
  if (groups && groups.length > 0) {
    for (const group of groups) {
      r += 1;
      lines.push(
        `<row r="${r}">${cell(`A${r}`, `${group.label} (${group.count})`, STYLE.bold)}</row>`,
      );
      for (const row of rows.slice(group.start, group.start + group.count)) dataRow(row);
    }
  } else {
    for (const row of rows) dataRow(row);
  }
  const widths = headers.map((h, i) => {
    const longest = Math.max(h.length, ...rows.map((row) => textLength(row[i])));
    return Math.min(60, Math.max(8, longest + 2));
  });
  const last = `${columnLetter(Math.max(0, headers.length - 1))}${r}`;
  const filter = groups && groups.length > 0 ? '' : `<autoFilter ref="A1:${last}"/>`;
  return (
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
    `<dimension ref="A1:${last}"/>` +
    '<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>' +
    '<sheetFormatPr defaultRowHeight="15"/>' +
    `<cols>${widths.map((w, i) => `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`).join('')}</cols>` +
    `<sheetData>${lines.join('')}</sheetData>` +
    filter +
    '</worksheet>'
  );
}

const STYLES_XML =
  '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
  '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
  '<numFmts count="2"><numFmt numFmtId="164" formatCode="yyyy-mm-dd"/><numFmt numFmtId="165" formatCode="yyyy-mm-dd hh:mm:ss"/></numFmts>' +
  '<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>' +
  '<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>' +
  '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>' +
  '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
  '<cellXfs count="4">' +
  '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>' +
  '<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>' +
  '<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>' +
  '<xf numFmtId="165" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>' +
  '</cellXfs>' +
  '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>' +
  '</styleSheet>';

const sheetName = (name: string) =>
  escapeXml(
    name
      .replace(/[[\]:*?/\\]/g, ' ')
      .trim()
      .slice(0, 31) || 'Data',
  );

/** The workbook as bytes. */
export function buildXlsx(table: SheetTable, date = new Date()): Uint8Array {
  const parts: { name: string; data: string }[] = [
    {
      name: '[Content_Types].xml',
      data:
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
        '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
        '<Default Extension="xml" ContentType="application/xml"/>' +
        '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
        '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>' +
        '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
        '</Types>',
    },
    {
      name: '_rels/.rels',
      data:
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
        '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>' +
        '</Relationships>',
    },
    {
      name: 'xl/workbook.xml',
      data:
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">' +
        `<sheets><sheet name="${sheetName(table.name)}" sheetId="1" r:id="rId1"/></sheets>` +
        '</workbook>',
    },
    {
      name: 'xl/_rels/workbook.xml.rels',
      data:
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
        '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>' +
        '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>' +
        '</Relationships>',
    },
    { name: 'xl/styles.xml', data: STYLES_XML },
    { name: 'xl/worksheets/sheet1.xml', data: sheetXml(table) },
  ];
  return zipStore(
    parts.map((part) => ({ name: part.name, data: encoder.encode(part.data) })),
    date,
  );
}
